// Centralized in-app navigation helper.
//
// The app uses hand-rolled route/workspace state (no React Router). Views and
// admin workspaces listen for the `punch:navigate` event and the browser
// `popstate` event to re-read the URL. This helper keeps history and that
// route state in sync so browser back/forward work reliably, without migrating
// the routing architecture.
//
// External links should NOT go through this helper — keep using plain anchors
// for full-page navigation to public/marketing/auth pages.

export const PUNCH_NAVIGATE_EVENT = 'punch:navigate'

export interface NavigateOptions {
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean
  /** History state payload (defaults to null). */
  state?: unknown
  /** Dispatch the `punch:navigate` event so listeners re-resolve the URL (default true). */
  notify?: boolean
}

const currentHref = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`

/**
 * Normalize a path or URL to a same-origin relative href. Absolute URLs that
 * point to another origin are returned untouched so the caller can detect and
 * avoid routing them through history.
 */
const resolveHref = (pathOrUrl: string): string => {
  try {
    const url = new URL(pathOrUrl, window.location.origin)
    if (url.origin !== window.location.origin) return url.href
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return pathOrUrl
  }
}

/**
 * Navigate within the app by updating browser history and notifying listeners.
 * Skips pushing a duplicate history entry when the target matches the current
 * URL, which prevents back-button "dead clicks" and navigation loops.
 */
export const navigateTo = (pathOrUrl: string, options: NavigateOptions = {}): void => {
  if (typeof window === 'undefined') return
  const { replace = false, state = null, notify = true } = options
  const target = resolveHref(pathOrUrl)

  if (target === currentHref()) {
    // Same URL: never stack a duplicate history entry. Listeners already
    // reflect this URL, so there is nothing to re-resolve.
    return
  }

  if (replace) {
    window.history.replaceState(state, '', target)
  } else {
    window.history.pushState(state, '', target)
  }

  if (notify) window.dispatchEvent(new Event(PUNCH_NAVIGATE_EVENT))
}
